/**
 * Race IQ Briefing.
 *
 * Source-linked F1 headlines with a derived, clearly-labeled analysis layer.
 * Facts (headline, source, date, snippet) come from RSS ingestion; the
 * "Race IQ read" section is a projection derived from category + entities and
 * is labeled as such. No article bodies, no scraped images, no republishing.
 */
import React, { useEffect, useMemo, useState } from "react";
import { ArrowUpRight, Search } from "lucide-react";
import { api, useFetch } from "../lib/api";
import { Reveal } from "../components/atoms";

const CATEGORY_ORDER = [
    "All",
    "Driver News",
    "Team News",
    "Technical Updates",
    "Upgrades",
    "Race Weekend",
    "Strategy",
    "Transfers / Contracts",
    "Regulation Changes",
    "Championship",
    "Race Results",
    "Reliability",
    "Penalties / Stewarding",
];

const TRUST_LABELS = {
    official: "Official source",
    established: "Established outlet",
    aggregator: "Secondary outlet",
};

export function normalizeBriefing(item = {}) {
    const category = item.category || item.tag || "Race Weekend";
    return {
        id: item.id || item.slug || item.headline,
        headline: item.headline || item.title || "",
        source: item.source || "Unknown source",
        trustTier: item.trust_tier || "aggregator",
        category: category === "Transfers" || category === "Contracts" ? "Transfers / Contracts" : category,
        publishedAt: item.published_at || null,
        url: item.source_url || "",
        author: item.author || null,
        summary: item.summary || "",
        analysis: item.analysis || null,
        teams: item.related_team_ids || [],
        drivers: item.related_driver_ids || [],
        tags: (item.tags || []).filter(Boolean),
    };
}

export function filterBriefings(briefings = [], category = "All", query = "") {
    const q = query.trim().toLowerCase();
    return briefings.filter((briefing) => {
        if (category !== "All" && briefing.category !== category) return false;
        if (!q) return true;
        const haystack = [
            briefing.headline,
            briefing.source,
            briefing.category,
            briefing.summary,
            briefing.tags.join(" "),
            briefing.teams.join(" "),
            briefing.drivers.join(" "),
        ]
            .join(" ")
            .toLowerCase();
        return haystack.includes(q);
    });
}

function formatDate(value) {
    if (!value) return "";
    try {
        return new Intl.DateTimeFormat(undefined, { month: "short", day: "numeric" }).format(new Date(value));
    } catch {
        return "";
    }
}

function hoursSince(value) {
    if (!value) return null;
    const ms = Date.now() - new Date(value).getTime();
    return Number.isFinite(ms) ? ms / 36e5 : null;
}

function TrustBadge({ tier }) {
    const tone =
        tier === "official"
            ? "border-f1-red/40 text-f1-red"
            : tier === "established"
                ? "border-white/20 text-zinc-300"
                : "border-white/10 text-zinc-500";
    return (
        <span className={`rounded border px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-[0.12em] ${tone}`}>
            {TRUST_LABELS[tier] || tier}
        </span>
    );
}

function BriefingListItem({ briefing, active, onSelect }) {
    return (
        <button
            type="button"
            data-testid="briefing-list-item"
            onClick={onSelect}
            className={`w-full border-l-2 px-4 py-3 text-left transition ${
                active
                    ? "border-f1-red bg-white/[0.04]"
                    : "border-transparent hover:border-white/20 hover:bg-white/[0.02]"
            }`}
        >
            <div className="flex items-baseline justify-between gap-3">
                <span className="font-mono text-[10px] uppercase tracking-[0.14em] text-zinc-500">
                    {briefing.category}
                </span>
                <span className="shrink-0 font-mono text-[10px] text-zinc-600">{formatDate(briefing.publishedAt)}</span>
            </div>
            <h3 className={`mt-1 text-[15px] leading-snug ${active ? "text-white" : "text-zinc-200"}`}>
                {briefing.headline}
            </h3>
            <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.12em] text-zinc-600">{briefing.source}</p>
        </button>
    );
}

function AnalysisRow({ label, children }) {
    if (!children) return null;
    return (
        <div className="grid gap-1 sm:grid-cols-[160px_1fr] sm:gap-4">
            <p className="font-mono text-[10px] uppercase leading-5 tracking-[0.14em] text-zinc-500">{label}</p>
            <p className="text-sm leading-6 text-zinc-300">{children}</p>
        </div>
    );
}

function BriefingDetail({ briefing }) {
    if (!briefing) return null;
    const analysis = briefing.analysis || {};
    const entities = [...briefing.teams, ...briefing.drivers];
    return (
        <article data-testid="briefing-detail" className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
                <span className="font-mono text-[10px] uppercase tracking-[0.16em] text-f1-red">{briefing.category}</span>
                <TrustBadge tier={briefing.trustTier} />
            </div>
            <h2 className="mt-3 font-display text-3xl leading-tight tracking-tight text-white sm:text-4xl">
                {briefing.headline}
            </h2>
            <p className="mt-2 text-sm text-zinc-500">
                {briefing.source}
                {briefing.author ? ` · ${briefing.author}` : ""}
                {briefing.publishedAt ? ` · ${formatDate(briefing.publishedAt)}` : ""}
            </p>

            {briefing.summary ? (
                <p className="mt-5 border-l-2 border-white/15 pl-4 text-[15px] leading-7 text-zinc-300">
                    {briefing.summary}
                </p>
            ) : null}

            <a
                href={briefing.url}
                target="_blank"
                rel="noreferrer"
                className="mt-4 inline-flex items-center gap-1.5 text-sm text-zinc-300 underline decoration-zinc-600 underline-offset-4 transition hover:text-white"
            >
                Read at {briefing.source}
                <ArrowUpRight size={14} />
            </a>

            {analysis && (analysis.why_it_matters || analysis.what_to_watch_next) ? (
                <section className="mt-8 border-t border-white/10 pt-6">
                    <div className="flex items-baseline justify-between gap-4">
                        <h3 className="font-mono text-[11px] uppercase tracking-[0.18em] text-zinc-400">Race IQ read</h3>
                        <span className="font-mono text-[10px] uppercase tracking-[0.12em] text-zinc-600">
                            Projection, not reporting
                        </span>
                    </div>
                    <div className="mt-5 space-y-4">
                        <AnalysisRow label="Why it matters">{analysis.why_it_matters}</AnalysisRow>
                        <AnalysisRow label="Possible impact">{analysis.possible_impact}</AnalysisRow>
                        <AnalysisRow label="Who is affected">{analysis.who_is_affected}</AnalysisRow>
                        <AnalysisRow label="What to watch next">{analysis.what_to_watch_next}</AnalysisRow>
                        {analysis.confidence ? (
                            <AnalysisRow label="Confidence">{`${analysis.confidence}${analysis.enhanced_by ? " · analyst model" : " · rule-based"}`}</AnalysisRow>
                        ) : null}
                    </div>
                </section>
            ) : null}

            {entities.length ? (
                <div className="mt-6 flex flex-wrap gap-2">
                    {entities.map((entity) => (
                        <span key={entity} className="rounded border border-white/10 px-2 py-1 font-mono text-[10px] uppercase tracking-[0.12em] text-zinc-400">
                            {entity.replace(/[-_]/g, " ")}
                        </span>
                    ))}
                </div>
            ) : null}
        </article>
    );
}

function EmptyState({ error, hasAnyData, hasFilters, onClear }) {
    let title = "No briefings yet";
    let body = "The ingestion pipeline hasn't published any briefings. Run a sync from the admin panel or `python backend/scripts/sync_news.py`.";
    if (error) {
        title = "Couldn't load briefings";
        body = "The briefing feed didn't respond. Refresh the page — if this keeps happening the backend may be down.";
    } else if (hasAnyData && hasFilters) {
        title = "No briefings match";
        body = "Nothing matches the current category or search. Clear the filters to see everything.";
    }
    return (
        <div data-testid="briefing-empty-state" className="border border-dashed border-white/10 px-6 py-16 text-center">
            <h3 className="font-display text-2xl text-white">{title}</h3>
            <p className="mx-auto mt-2 max-w-md text-sm leading-6 text-zinc-500">{body}</p>
            {hasAnyData && hasFilters ? (
                <button
                    type="button"
                    onClick={onClear}
                    className="mt-4 rounded border border-white/15 px-4 py-2 text-sm text-zinc-300 transition hover:border-white/30 hover:text-white"
                >
                    Clear filters
                </button>
            ) : null}
        </div>
    );
}

export default function News() {
    const { data: newsResp, loading, error } = useFetch(() => api.news(80), []);
    const [category, setCategory] = useState("All");
    const [query, setQuery] = useState("");
    const [activeBriefingId, setActiveBriefingId] = useState("");

    const briefings = useMemo(
        () => (newsResp?.news || []).map(normalizeBriefing).filter((briefing) => briefing.headline),
        [newsResp?.news]
    );

    const categories = useMemo(() => {
        const present = new Set(briefings.map((briefing) => briefing.category));
        return CATEGORY_ORDER.filter((name) => name === "All" || present.has(name));
    }, [briefings]);

    const filtered = useMemo(() => filterBriefings(briefings, category, query), [briefings, category, query]);

    useEffect(() => {
        if (!filtered.length) return;
        if (!filtered.some((briefing) => briefing.id === activeBriefingId)) {
            setActiveBriefingId(filtered[0].id);
        }
    }, [activeBriefingId, filtered]);

    const activeBriefing = useMemo(
        () => filtered.find((briefing) => briefing.id === activeBriefingId) || filtered[0] || null,
        [activeBriefingId, filtered]
    );

    const newestAgeHours = hoursSince(briefings[0]?.publishedAt);
    const hasFilters = category !== "All" || Boolean(query.trim());

    return (
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8" data-testid="race-iq-briefing-page">
            <Reveal>
                <header className="mb-8">
                    <span className="label-eyebrow text-f1-red">Race IQ Briefing</span>
                    <h1 className="mt-2 font-display text-5xl leading-none tracking-tight sm:text-6xl">Briefing</h1>
                    <p className="mt-3 max-w-2xl text-zinc-400">
                        F1 headlines with the race-intelligence read: what happened, why it matters,
                        and what to watch next. Sources linked, projections labeled.
                    </p>
                    {briefings.length > 0 && newestAgeHours != null && newestAgeHours > 48 ? (
                        <p className="mt-3 font-mono text-[11px] uppercase tracking-[0.14em] text-amber-500/80">
                            Last update {Math.round(newestAgeHours / 24)}d ago — feed may be stale
                        </p>
                    ) : null}
                </header>
            </Reveal>

            <div className="mb-6 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                <div className="flex flex-wrap gap-1.5" data-testid="briefing-category-filters">
                    {categories.map((name) => (
                        <button
                            key={name}
                            type="button"
                            onClick={() => setCategory(name)}
                            className={`rounded px-2.5 py-1.5 font-mono text-[10px] uppercase tracking-[0.12em] transition ${
                                category === name
                                    ? "bg-f1-red text-white"
                                    : "border border-white/10 text-zinc-400 hover:border-white/25 hover:text-zinc-200"
                            }`}
                        >
                            {name}
                        </button>
                    ))}
                </div>
                <label className="relative block lg:w-64">
                    <Search size={14} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
                    <input
                        type="search"
                        value={query}
                        onChange={(event) => setQuery(event.target.value)}
                        placeholder="Search briefings…"
                        data-testid="briefing-search"
                        className="w-full rounded border border-white/10 bg-transparent py-2 pl-9 pr-3 text-sm text-zinc-200 placeholder:text-zinc-600 focus:border-white/30 focus:outline-none"
                    />
                </label>
            </div>

            {loading ? (
                <div className="space-y-3" aria-busy="true">
                    {[0, 1, 2, 3].map((row) => (
                        <div key={row} className="h-16 animate-pulse rounded bg-white/[0.04]" />
                    ))}
                </div>
            ) : filtered.length === 0 ? (
                <EmptyState
                    error={Boolean(error)}
                    hasAnyData={briefings.length > 0}
                    hasFilters={hasFilters}
                    onClear={() => {
                        setCategory("All");
                        setQuery("");
                    }}
                />
            ) : (
                <div className="grid gap-10 lg:grid-cols-[380px_minmax(0,1fr)]">
                    <div className="max-h-[70vh] divide-y divide-white/[0.06] overflow-y-auto border-y border-white/[0.06]">
                        {filtered.map((briefing) => (
                            <BriefingListItem
                                key={briefing.id}
                                briefing={briefing}
                                active={activeBriefing?.id === briefing.id}
                                onSelect={() => setActiveBriefingId(briefing.id)}
                            />
                        ))}
                    </div>
                    <BriefingDetail briefing={activeBriefing} />
                </div>
            )}
        </div>
    );
}
